import type { QueryClient } from "@tanstack/react-query";

import type { ApiPlansPayload, ProfileInformation, User } from "@/types/models";
import type { ServiceResponse } from "./api.types";
import { privateAPI } from "./axios";

const DOMAIN = "user";
const ALL = "all";

export const getUser = async (
  userId: User["id"] | undefined,
): Promise<User> => {
  const response = await privateAPI.get<ServiceResponse<User>>(
    `/users/${userId}`,
  );

  return response.data.data;
};

export interface UserFilters {
  country?: string[];
  role?: string;
  plan?: string[];
  hasPartner?: string;
  registeredFrom?: string;
  registeredTo?: string;
}

export const getUsers = async (
  page: number,
  searchQuery = "",
  locked: string | number = "",
  filters: UserFilters = {},
  sort?: string,
): Promise<ServiceResponse<User[]>> => {
  const params: Record<string, string | number> = {
    page,
  };

  if (locked !== "") {
    params["filter[locked]"] = locked;
  }

  if (searchQuery) {
    params["filter[email]"] = searchQuery;
  }

  if (filters.country?.length) {
    params["filter[country]"] = filters.country.join(",");
  }

  if (filters.role) {
    params["filter[role]"] = filters.role;
  }

  if (filters.plan?.length) {
    params["filter[plan]"] = filters.plan.join(",");
  }

  if (filters.hasPartner) {
    params["filter[has_partner]"] = filters.hasPartner;
  }

  if (filters.registeredFrom) {
    params["filter[registered_from]"] = filters.registeredFrom;
  }

  if (filters.registeredTo) {
    params["filter[registered_to]"] = filters.registeredTo;
  }

  if (sort) {
    params.sort = sort;
  }

  const response = await privateAPI.get<ServiceResponse<User[]>>(`/users`, {
    params,
  });

  return response.data;
};

export const getUserCountries = async (): Promise<string[]> => {
  const response =
    await privateAPI.get<ServiceResponse<string[]>>(`/users/countries`);

  return response.data.data;
};

interface CreateUserParams {
  name: string;
  email: string;
  password: string;
  passwordConfirmation: string;
}

interface ProfileParams {
  profile: ProfileInformation;
  userId: User["id"] | undefined;
}

interface LockUserParams {
  userId?: User["id"];
  locked?: boolean;
}

export const createUser = {
  mutation: async (params: CreateUserParams) => {
    const { passwordConfirmation, ...rest } = params;
    const response = await privateAPI.post<ServiceResponse<User>>("/users", {
      ...rest,
      password_confirmation: passwordConfirmation,
    });

    return response.data.data;
  },
  invalidates: (queryClient: QueryClient) => {
    void queryClient.invalidateQueries({ queryKey: [DOMAIN, "getUserQuery"] });
  },
};

export const updateUser = async ({
  profile,
  userId,
}: ProfileParams): Promise<User> => {
  if (!userId) return Promise.reject("No User found");
  const response = await privateAPI.put<ServiceResponse<User>>(
    `/users/${userId}`,
    {
      ...profile,
    },
  );

  return response.data.data;
};

export const patchUser = async ({ userId, locked }: LockUserParams) => {
  if (!userId) return Promise.reject("No User found");
  const response = await privateAPI.patch<ServiceResponse<User>>(
    `/users/${userId}`,
    {
      locked,
    },
  );

  return response.data.data;
};

interface AccessLevelParams {
  userId: User["id"] | undefined;
  accessLevels: ApiPlansPayload;
}

export const updateUserAccessLevels = async ({
  userId,
  accessLevels,
}: AccessLevelParams): Promise<User> => {
  if (!userId) return Promise.reject("No User found");
  const response = await privateAPI.put<ServiceResponse<User>>(
    `/users/${userId}/free-plans`,
    {
      ...accessLevels,
    },
  );

  return response.data.data;
};

export const exportUsers = async (plan?: string): Promise<void> => {
  const params: Record<string, string> = {};
  if (plan) params.plan = plan;

  const response = await privateAPI.get("/users/export", {
    params,
    responseType: "blob",
  });

  const url = window.URL.createObjectURL(new Blob([response.data as BlobPart]));
  const link = document.createElement("a");
  link.href = url;
  const date = new Date().toISOString().slice(0, 10);
  link.setAttribute("download", `users_${date}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const deleteUser = {
  mutation: async (userId: User["id"]) => {
    await privateAPI.delete(`/users/${userId}`);
  },
  invalidates: (
    queryClient: QueryClient,
    { userId }: { userId: User["id"] },
  ) => {
    void queryClient.invalidateQueries({ queryKey: [DOMAIN, ALL] });
    void queryClient.invalidateQueries({ queryKey: [DOMAIN, userId] });
  },
};
